import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HelpCircle, Minus, X, Send, RotateCcw, ChevronLeft, List } from 'lucide-react';
import { apiJson } from '../lib/api';

// The ready-made answers (lib/helpFaq, ~90 kB) load the first time the chat
// opens instead of riding along with every page. A failed download (e.g. an
// old tab after a deploy) is retried on the next attempt.
let faqPromise = null;
const loadFaq = () => {
  if (!faqPromise) faqPromise = import('../lib/helpFaq').catch((e) => { faqPromise = null; throw e; });
  return faqPromise;
};

// In-app help assistant ("Asistente zBilling"). A floating help button on the
// right that opens a chat panel; users ask how to do things in the app and get
// a ready-made answer (lib/helpFaq — no AI, no cost) in es/ca/en/ur, with links
// to the pages involved. Accounts with the 'help_assistant' module also send
// questions that have no ready answer to the AI assistant (/api/help/chat).
//
// Three faces, remembered per browser:
//   'button' — round help button, bottom-right
//   'open'   — the chat panel
//   'min'    — minimized to a slim tab on the right edge
// The whole widget is switched on/off from the header (Layout's headset icon).

const VIEW_KEY = 'zb_helpbot_view';
const LANG_KEY = 'zb_helpbot_lang';
// The conversation lives per tab: it survives navigating between pages but
// not closing the tab.
const CHAT_KEY = 'zb_helpbot_chat';
// Only the most recent turns go to the AI as context.
const MAX_HISTORY = 20;
const MAX_QUESTION = 1000;

// Answer languages are shown by their own names, whatever the UI language.
const ANSWER_LANGS = [
  { code: 'es', label: 'Español' },
  { code: 'ca', label: 'Català' },
  { code: 'en', label: 'English' },
  { code: 'ur', label: 'اردو' },
];

// First topics offered in an empty chat.
const POPULAR = ['invoice-create', 'estimate-to-invoice', 'business-data-logo', 'team-invite', 'tpv-sell', 'expense-add'];
const AREAS = ['documents', 'operations', 'settings'];

// Chat texts in Urdu — the app has no Urdu locale, so they live here.
const UR = {
  noMatch: 'اس کا تیار جواب میرے پاس نہیں ہے۔ دوسرے الفاظ آزمائیں یا نیچے سے کوئی موضوع چنیں۔ آپ info@zcom.es پر لکھ سکتے ہیں یا 935 319 064 پر کال کر سکتے ہیں۔',
  didYouMean: 'کیا آپ کا مطلب ان میں سے کوئی موضوع ہے؟',
  allTopics: 'تمام موضوعات دیکھیں',
  allTopicsIntro: 'یہ مدد کے تمام موضوعات ہیں:',
  related: 'متعلقہ موضوعات',
  smallHello: 'السلام علیکم! میں آپ کی کس طرح مدد کروں؟ اپنا سوال لکھیں یا نیچے سے کوئی موضوع چنیں۔',
  smallThanks: 'آپ کا شکریہ! کچھ اور پوچھنا ہو تو میں یہیں ہوں۔',
  area_documents: 'دستاویزات اور کیٹلاگ',
  area_operations: 'سیلز، اخراجات اور مالیات',
  area_settings: 'اکاؤنٹ اور سیٹنگز',
};

const readStore = (store, key, fallback) => {
  try { return store.getItem(key) ?? fallback; } catch { return fallback; }
};
const writeStore = (store, key, value) => {
  try { store.setItem(key, value); } catch { /* private mode / blocked storage */ }
};

// ── Minimal, safe Markdown ───────────────────────────────────────────────────
// Answers use **bold**, numbered/bulleted lists and [label](/route) links to app
// pages. Rendered as React nodes (never innerHTML); only in-app paths become
// links, so an answer can't send anyone off-site.
const INLINE_RE = /\*\*([^*]+)\*\*|\[([^\]]+)\]\((\/[^)\s]*)\)/g;

function renderInline(text, onNavigate) {
  const out = [];
  let last = 0;
  let m;
  INLINE_RE.lastIndex = 0;
  while ((m = INLINE_RE.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1]) {
      out.push(<strong key={m.index} className="font-semibold text-[#1d2d3d]">{m[1]}</strong>);
    } else {
      const path = m[3];
      out.push(
        <button
          key={m.index}
          type="button"
          onClick={() => onNavigate(path)}
          className="font-semibold text-[#1a4f8b] underline underline-offset-2 hover:text-[#fca311]"
        >
          {m[2]}
        </button>
      );
    }
    last = INLINE_RE.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function Markdown({ text, onNavigate }) {
  const blocks = [];
  let list = null; // { ordered, items[] }
  const flush = () => { if (list) { blocks.push(list); list = null; } };
  (text || '').split('\n').forEach((raw) => {
    const line = raw.trimEnd();
    const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
    const numbered = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (bullet || numbered) {
      const ordered = !!numbered;
      if (!list || list.ordered !== ordered) { flush(); list = { ordered, items: [] }; }
      list.items.push((bullet || numbered)[1]);
      return;
    }
    flush();
    if (!line.trim()) return;
    blocks.push({ p: line.replace(/^#{1,6}\s+/, '') });
  });
  flush();

  return blocks.map((b, i) => {
    if (b.items) {
      const Tag = b.ordered ? 'ol' : 'ul';
      return (
        <Tag key={i} className={`${b.ordered ? 'list-decimal' : 'list-disc'} ps-5 my-1.5 space-y-1`}>
          {b.items.map((it, j) => <li key={j}>{renderInline(it, onNavigate)}</li>)}
        </Tag>
      );
    }
    return <p key={i} className="my-1.5 first:mt-0 last:mb-0">{renderInline(b.p, onNavigate)}</p>;
  });
}

export default function HelpBot({ enabled, aiEnabled, fiscalCountry, userRole, onNavigate }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [view, setView] = useState(() => readStore(localStorage, VIEW_KEY, 'button'));
  const [answerLang, setAnswerLang] = useState(() => readStore(localStorage, LANG_KEY, 'auto'));
  const [messages, setMessages] = useState(() => {
    try { return JSON.parse(readStore(sessionStorage, CHAT_KEY, '[]')) || []; } catch { return []; }
  });
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [faq, setFaq] = useState(null); // lib/helpFaq once loaded
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { writeStore(localStorage, VIEW_KEY, view); }, [view]);
  useEffect(() => { writeStore(localStorage, LANG_KEY, answerLang); }, [answerLang]);
  useEffect(() => { writeStore(sessionStorage, CHAT_KEY, JSON.stringify(messages)); }, [messages]);

  // Keep the newest message in view.
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, busy, view]);

  useEffect(() => {
    if (view === 'open' && inputRef.current) inputRef.current.focus();
  }, [view]);

  useEffect(() => {
    if (view === 'open' && !faq) loadFaq().then((m) => setFaq(m)).catch(() => {});
  }, [view, faq]);

  if (!enabled) return null;

  const country = fiscalCountry || 'ES';
  const uiLang = ['es', 'ca', 'en'].includes((i18n.language || '').slice(0, 2)) ? i18n.language.slice(0, 2) : 'es';
  // Language for topic buttons shown before the user has typed anything.
  const chipLang = answerLang === 'auto' ? uiLang : answerLang;
  const say = (key, lang) => (lang === 'ur' ? UR[key] : t('helpbot.' + key, { lng: lang }));
  const pick = (lib, id) => {
    const e = lib?.faqById(id);
    return e && (!e.countries || e.countries.includes(country)) ? e : null;
  };
  const available = (id) => pick(faq, id);
  const push = (msg) => setMessages((m) => [...m, msg]);

  // On the TPV the round button would sit over the till's own buttons, so it
  // folds to the edge tab there; an explicitly opened panel stays open.
  const onTpv = location.pathname.startsWith('/tpv');
  const face = view === 'button' && onTpv ? 'min' : view;

  const go = (path) => {
    // Small screens: the panel covers the page, so get out of the way.
    if (window.innerWidth < 640) setView('button');
    onNavigate(path);
  };

  const showTopics = () => push({ role: 'assistant', local: true, topics: true, lang: chipLang, content: say('allTopicsIntro', chipLang) });

  // `presetId` / `presetLang` come from a topic button: answer that entry, in
  // the language the button was shown in.
  const send = async (preset, presetId, presetLang) => {
    const q = (preset ?? input).trim().slice(0, MAX_QUESTION);
    if (!q || busy) return;
    let lib = faq;
    if (!lib) {
      try { lib = await loadFaq(); setFaq(lib); } catch { return; } // input kept for a retry
    }
    const lang = presetLang || (answerLang === 'auto' ? lib.detectLang(q, uiLang) : answerLang);
    const history = [...messages, { role: 'user', content: q }];
    setMessages(history);
    setInput('');

    const kind = !presetId && lib.smallTalk(q);
    if (kind) {
      push(kind === 'hello'
        ? { role: 'assistant', local: true, lang, content: say('smallHello', lang), chips: POPULAR }
        : { role: 'assistant', local: true, lang, content: say('smallThanks', lang) });
      return;
    }

    const hit = presetId ? { best: pick(lib, presetId), others: [] } : lib.matchFaq(q, { country });
    if (hit.best) {
      push({ role: 'assistant', local: true, lang, content: hit.best.a[lang] || hit.best.a.en, chips: hit.best.related, chipsLabel: true });
      return;
    }

    // No ready answer: accounts with the AI module ask the assistant.
    if (aiEnabled) {
      setBusy(true);
      try {
        const res = await apiJson('/api/help/chat', {
          method: 'POST',
          body: {
            messages: history.filter((m) => !m.local).slice(-MAX_HISTORY)
              .map(({ role, content }) => ({ role, content })),
            answer_lang: answerLang,
            ui_lang: uiLang,
            context: { path: location.pathname + location.search, country, role: userRole || '' },
          },
        });
        if (res?.reply) { push({ role: 'assistant', content: res.reply }); return; }
      } catch { /* fall back to the topic list below */ } finally {
        setBusy(false);
      }
    }

    push(hit.others.length
      ? { role: 'assistant', local: true, lang, content: say('didYouMean', lang), chips: hit.others.map((e) => e.id) }
      : { role: 'assistant', local: true, lang, content: say('noMatch', lang), chips: POPULAR });
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
    if (e.key === 'Escape') setView('button');
  };

  const chip = (entry, lang) => (
    <button
      key={entry.id}
      type="button"
      dir="auto"
      onClick={() => send(entry.q[lang] || entry.q.en, entry.id, lang)}
      className="text-start text-xs font-semibold text-[#1a4f8b] bg-blue-50 hover:bg-blue-100 border border-blue-100 rounded-full px-3 py-1.5 transition-colors"
    >
      {entry.q[lang] || entry.q.en}
    </button>
  );

  const chipGroup = (key, label, entries, lang) => (entries.length ? (
    <div key={key} className="mt-2.5">
      {label && <p className="text-[10px] font-bold uppercase tracking-wide text-[#9aa7b2] mb-1">{label}</p>}
      <div className="flex flex-wrap gap-1.5">{entries.map((e) => chip(e, lang))}</div>
    </div>
  ) : null);

  if (face === 'min') {
    return (
      <button
        type="button"
        onClick={() => setView('open')}
        title={t('helpbot.restore')}
        aria-label={t('helpbot.restore')}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-[55] flex flex-col items-center gap-1 bg-[#1a4f8b] hover:bg-[#163f70] text-white rounded-l-xl ps-1.5 pe-1 py-3 shadow-lg transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        <HelpCircle className="w-5 h-5" />
      </button>
    );
  }

  if (face === 'button') {
    return (
      <div className="group fixed bottom-5 right-5 z-[55]">
        <button
          type="button"
          onClick={() => setView('open')}
          title={t('helpbot.title')}
          aria-label={t('helpbot.title')}
          className="flex items-center gap-2 h-12 ps-3.5 pe-4 rounded-full bg-[#1a4f8b] hover:bg-[#163f70] text-white font-bold text-sm shadow-xl transition-colors"
        >
          <HelpCircle className="w-5 h-5" />
          <span className="hidden sm:inline">{t('helpbot.open')}</span>
        </button>
        <button
          type="button"
          onClick={() => setView('min')}
          title={t('helpbot.minimize')}
          aria-label={t('helpbot.minimize')}
          className="absolute -top-2 -left-2 w-5 h-5 rounded-full bg-white border border-[#e1e5e8] text-[#6d7d8b] hover:text-[#1a4f8b] shadow flex items-center justify-center sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 transition-opacity"
        >
          <Minus className="w-3 h-3" />
        </button>
      </div>
    );
  }

  const popular = POPULAR.map(available).filter(Boolean);

  return (
    <div
      role="dialog"
      aria-label={t('helpbot.title')}
      className="fixed bottom-4 right-4 z-[55] w-[min(390px,calc(100vw-2rem))] h-[min(580px,calc(100vh-6rem))] bg-white border border-[#e1e5e8] rounded-2xl shadow-2xl flex flex-col overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center gap-2.5 px-4 py-3 bg-[#1a4f8b] text-white">
        <HelpCircle className="w-5 h-5 flex-shrink-0 text-[#fca311]" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold leading-tight truncate">{t('helpbot.title')}</p>
          <p className="text-[11px] text-white/75 leading-tight truncate">{t('helpbot.subtitle')}</p>
        </div>
        <button type="button" onClick={showTopics} title={t('helpbot.allTopics')} aria-label={t('helpbot.allTopics')} className="p-1.5 rounded-lg hover:bg-white/15">
          <List className="w-4 h-4" />
        </button>
        {messages.length > 0 && (
          <button type="button" onClick={() => setMessages([])} title={t('helpbot.newChat')} aria-label={t('helpbot.newChat')} className="p-1.5 rounded-lg hover:bg-white/15">
            <RotateCcw className="w-4 h-4" />
          </button>
        )}
        <button type="button" onClick={() => setView('min')} title={t('helpbot.minimize')} aria-label={t('helpbot.minimize')} className="p-1.5 rounded-lg hover:bg-white/15">
          <Minus className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => setView('button')} title={t('helpbot.close')} aria-label={t('helpbot.close')} className="p-1.5 rounded-lg hover:bg-white/15">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Answer language */}
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-[#e1e5e8] bg-gray-50 overflow-x-auto">
        <span className="text-[11px] font-semibold text-[#6d7d8b] whitespace-nowrap me-0.5">{t('helpbot.answerLang')}</span>
        {[{ code: 'auto', label: t('helpbot.langAuto') }, ...ANSWER_LANGS].map((l) => (
          <button
            key={l.code}
            type="button"
            onClick={() => setAnswerLang(l.code)}
            className={`px-2 py-0.5 rounded-full text-[11px] font-bold whitespace-nowrap border transition-colors ${
              answerLang === l.code
                ? 'bg-[#1a4f8b] border-[#1a4f8b] text-white'
                : 'bg-white border-[#e1e5e8] text-[#6d7d8b] hover:text-[#1a4f8b]'
            }`}
          >
            {l.label}
          </button>
        ))}
      </div>

      {/* Conversation */}
      <div ref={listRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-[#f8fafc]">
        <div className="max-w-[90%] rounded-2xl rounded-ss-sm bg-white border border-[#e1e5e8] px-3 py-2 text-sm text-[#1d2d3d]">
          {t('helpbot.greeting')}
        </div>
        {messages.length === 0 && (
          <div className="flex flex-wrap gap-1.5">
            {popular.map((e) => chip(e, chipLang))}
            <button
              type="button"
              onClick={showTopics}
              className="inline-flex items-center gap-1 text-xs font-bold text-white bg-[#1a4f8b] hover:bg-[#163f70] rounded-full px-3 py-1.5 transition-colors"
            >
              <List className="w-3.5 h-3.5" /> {say('allTopics', chipLang)}
            </button>
          </div>
        )}
        {messages.map((m, i) => (
          m.role === 'user' ? (
            <div key={i} dir="auto" className="ms-auto max-w-[85%] w-fit rounded-2xl rounded-se-sm bg-[#1a4f8b] text-white px-3 py-2 text-sm whitespace-pre-wrap break-words">
              {m.content}
            </div>
          ) : (
            <div key={i} dir="auto" className="max-w-[90%] w-fit rounded-2xl rounded-ss-sm bg-white border border-[#e1e5e8] px-3 py-2 text-sm text-[#1d2d3d] break-words leading-relaxed">
              <Markdown text={m.content} onNavigate={go} />
              {m.chips && chipGroup('chips', m.chipsLabel ? say('related', m.lang) : null, m.chips.map(available).filter(Boolean), m.lang)}
              {m.topics && AREAS.map((a) => chipGroup(a, say('area_' + a, m.lang), (faq ? faq.faqForCountry(country) : []).filter((e) => e.area === a), m.lang))}
            </div>
          )
        ))}
        {busy && (
          <div className="w-fit rounded-2xl rounded-ss-sm bg-white border border-[#e1e5e8] px-3 py-2.5 flex items-center gap-1" aria-label={t('helpbot.thinking')}>
            {[0, 150, 300].map((d) => (
              <span key={d} className="w-1.5 h-1.5 rounded-full bg-[#1a4f8b] animate-bounce" style={{ animationDelay: `${d}ms` }} />
            ))}
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-[#e1e5e8] p-2.5 bg-white">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            dir="auto"
            rows={1}
            value={input}
            maxLength={MAX_QUESTION}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t('helpbot.placeholder')}
            className="flex-1 resize-none max-h-28 min-h-[40px] rounded-xl border border-[#e1e5e8] px-3 py-2 text-sm text-[#1d2d3d] focus:outline-none focus:border-[#1a4f8b] focus:ring-1 focus:ring-[#1a4f8b]"
          />
          <button
            type="button"
            onClick={() => send()}
            disabled={busy || !input.trim()}
            title={t('helpbot.send')}
            aria-label={t('helpbot.send')}
            className="h-10 w-10 flex-shrink-0 rounded-xl bg-[#fca311] hover:bg-orange-500 disabled:opacity-40 text-white flex items-center justify-center transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        {aiEnabled && <p className="mt-1.5 text-[10px] text-[#9aa7b2] text-center">{t('helpbot.disclaimer')}</p>}
      </div>
    </div>
  );
}
